// constants
import {
  ContentfulTemplates,
  ContentfulLocales,
  LOCALES,
  LocaleMap,
  AnotherLocaleMap,
} from 'src/lib/constants';

import type { CmsClient } from 'src/lib/contentful-client';

// helpers
import { marketingCmsClient } from 'src/lib/contentful-client';
import { optimizeContentfulImages } from './image-helpers';

export const makeNewTemplateRequestsForAllLocales = (args: any = {}) => {
  const requests = [];
  const { queryByLocale, ...restArgs } = args;
  ContentfulLocales.forEach((locale) => {
    requests.push(
      marketingCmsClient.getEntries({
        content_type: ContentfulTemplates.PAGE,
        include: 10,
        limit: 1000,
        locale,
        ...(queryByLocale?.[locale] ?? {}),
        ...restArgs,
      }),
    );
  });
  return requests;
};

/**
 * `client` exists so Timeline Preview can pass a release-scoped client built by
 * `createCmsClients({ timelinePreview })`. Those clients are per request, so
 * they cannot be reached through the module-level singleton.
 */
export const makeNewTemplateRequestsForSingleLocale = (
  locale: string,
  args = {},
  client: CmsClient = marketingCmsClient,
) => {
  return client.getEntries({
    content_type: ContentfulTemplates.PAGE,
    include: 10,
    limit: 1,
    locale,
    ...args,
  });
};

export const pathsAreIdentical = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((element, index) => {
    return element === arr2[index];
  });
};

/** See `makeNewTemplateRequestsForSingleLocale` for why `client` is injectable. */
export const makeSingleLegacyLocalizedRequest = ({
  contentType,
  locale,
  pageSlug,
  client = marketingCmsClient,
}: {
  contentType: string;
  locale: string;
  pageSlug: string;
  client?: CmsClient;
}) => {
  return client.getEntries({
    'fields.locale': locale,
    'fields.pageSlug': pageSlug,
    content_type: contentType,
    include: 10,
    limit: 1000,
  });
};

export const getLegacyLocaleFromPath = (path) => {
  const locale = path?.[0]?.toLowerCase();
  const matchingLocale = Object.entries(LocaleMap).find(([key, value]) => {
    return value === locale;
  })?.[0];
  return matchingLocale?.toUpperCase() || 'US';
};

export const getLegacyPageSlugFromPath = (path) => {
  return path[path.length - 1];
};

export const getLocaleFromPath = (path) => {
  const locale = path?.[0]?.toLowerCase();
  return AnotherLocaleMap[locale] ?? LOCALES.EN_US;
};

export const mapEntryToProps = ({ entry, preview }) => {
  return {
    props: { ...optimizeContentfulImages(entry), preview: !!preview },
  };
};

export const makeLegacyLocalizedRequests = ({ contentType, include }) => {
  const inclusions = new Set(include);
  const requests = [];
  const fauxLocales = Object.keys(LocaleMap).filter((locale) => {
    return inclusions.has(locale.toUpperCase());
  });
  fauxLocales.forEach((locale) => {
    const normalizedLocale = locale.toUpperCase();
    requests.push(
      marketingCmsClient.getEntries({
        'fields.locale': normalizedLocale,
        content_type: contentType,
        include: 1,
        limit: 1000,
      }),
    );
  });
  return requests;
};

export const mapResponseToPaths = (responses) => {
  const contentTypeToSubfolder = {
    [ContentfulTemplates.PRODUCT_PAGE]: 'products',
    [ContentfulTemplates.USE_CASE]: 'use-cases',
    [ContentfulTemplates.LANDING_PAGE]: null,
  };

  const paths = [];
  responses.forEach((response) => {
    response.items.forEach((item) => {
      const subfolder = contentTypeToSubfolder[item?.sys?.contentType?.sys?.id];
      const url = [
        subfolder,
        item?.fields?.pageSlug,
        ...(item?.fields?.url ?? []),
      ];
      const legacyLocale = item?.fields?.locale;
      if (legacyLocale && legacyLocale !== 'US') {
        url.unshift(LocaleMap[legacyLocale?.toLowerCase()]);
      }
      const locale = item?.sys?.locale;
      if (locale && locale !== LOCALES.EN_US) {
        url.unshift(locale.toLowerCase());
      }
      paths.push({
        params: {
          id: url.filter((elem) => {
            return !!elem;
          }),
        },
      });
    });
  });

  return paths;
};

export const findMatchingHardcodedPage = ({ pages, locale, slug }) => {
  return pages.find((page) => {
    return (
      page.params.id === LocaleMap[locale.toLowerCase()] &&
      page.params.index === slug
    );
  });
};

export const getPathWithoutLocale = (path) => {
  return path.filter((path) => {
    return !new RegExp(Array.from(ContentfulLocales).join('|'), 'i').test(path);
  });
};
